/**
 * Centralized connectivity quality manager for CrewPilot.
 *
 * States:
 * - good: network is healthy; sync and remote reads are allowed
 * - poor: weak/unstable connection; behaves like offline until proven stable
 * - offline: browser reports no network
 *
 * Transport priority (Wi-Fi before cellular):
 * 1. When Wi-Fi is the active transport, verify reachability over Wi-Fi first.
 * 2. Indirect failures (e.g. sync) do not downgrade while Wi-Fi is active —
 *    the health probe remains authoritative.
 * 3. When Wi-Fi reachability fails, fall back when the OS switches to cellular.
 * 4. Offline-first behavior applies only when neither transport is reliable.
 *
 * Thresholds:
 * - Two consecutive failures are required before entering poor.
 * - One successful health probe restores good immediately.
 * - Non-health / slow-path recovery still requires consecutive successes.
 */

export const CONNECTIVITY_STATE = Object.freeze({
  GOOD: "good",
  POOR: "poor",
  OFFLINE: "offline",
});

export const TRANSPORT_TYPE = Object.freeze({
  WIFI: "wifi",
  CELLULAR: "cellular",
  NONE: "none",
  UNKNOWN: "unknown",
});

export const CONNECTIVITY_CHANGED_EVENT = "cp-connectivity-changed";

const CONFIG = Object.freeze({
  HEALTH_CHECK_INTERVAL_GOOD_MS: 60_000,
  HEALTH_CHECK_INTERVAL_POOR_MS: 15_000,
  HEALTH_CHECK_TIMEOUT_MS: 10_000,
  SLOW_REQUEST_LATENCY_MS: 8_000,
  FAILURES_TO_POOR: 2,
  /** Non-health / slow-path recovery still requires consecutive successes. */
  SUCCESSES_TO_GOOD: 3,
  /** A successful health probe restores good immediately. */
  HEALTH_SUCCESSES_TO_GOOD: 1,
});

let state = resolveInitialState();
let consecutiveSuccesses = 0;
let consecutiveFailures = 0;
let healthCheckTimer = null;
let healthCheckInFlight = false;
let started = false;
let wifiReachabilityFailed = false;
let connectionChangeHandler = null;
let lastTransition = null;
let lastFailure = null;
const listeners = new Set();

function getNetworkConnection() {
  if (typeof navigator === "undefined") {
    return null;
  }

  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  );
}

/** Active data transport reported by the Network Information API. */
export function getActiveTransport() {
  if (isBrowserOffline()) {
    return TRANSPORT_TYPE.NONE;
  }

  const type = getNetworkConnection()?.type;
  if (type === "wifi") {
    return TRANSPORT_TYPE.WIFI;
  }
  if (type === "cellular") {
    return TRANSPORT_TYPE.CELLULAR;
  }
  if (type === "none") {
    return TRANSPORT_TYPE.NONE;
  }

  return TRANSPORT_TYPE.UNKNOWN;
}

function resolveInitialState() {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return CONNECTIVITY_STATE.OFFLINE;
  }
  return CONNECTIVITY_STATE.GOOD;
}

function isBrowserOffline() {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

function nowIso() {
  return new Date().toISOString();
}

function buildDiagnosticFields({
  reason = null,
  source = null,
  httpStatus = null,
  latencyMs = null,
  fromHealthCheck = false,
} = {}) {
  return {
    reason,
    source,
    httpStatus,
    latencyMs,
    activeTransport: getActiveTransport(),
    browserOnline: !isBrowserOffline(),
    fromHealthCheck: Boolean(fromHealthCheck),
    consecutiveFailures,
    consecutiveSuccesses,
    wifiReachabilityFailed,
    timestamp: nowIso(),
  };
}

function recordLastFailure(fields) {
  lastFailure = buildDiagnosticFields(fields);
}

function logConnectivityEvent(message, detail) {
  // Production-safe: no API keys, tokens, or full request URLs.
  console.warn(`[ConnectivityManager] ${message}`, detail);
}

function dispatchConnectivityChanged(previousState) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(CONNECTIVITY_CHANGED_EVENT, {
        detail: { state, previousState, lastTransition, lastFailure },
      }),
    );
  }

  for (const listener of listeners) {
    try {
      listener(state, previousState);
    } catch (error) {
      console.error("[ConnectivityManager] listener error:", error);
    }
  }
}

function setState(
  nextState,
  {
    reason = null,
    source = null,
    httpStatus = null,
    latencyMs = null,
    fromHealthCheck = false,
  } = {},
) {
  if (state === nextState) {
    return;
  }

  const previousState = state;
  state = nextState;
  lastTransition = {
    from: previousState,
    to: nextState,
    ...buildDiagnosticFields({
      reason,
      source,
      httpStatus,
      latencyMs,
      fromHealthCheck,
    }),
  };

  logConnectivityEvent(`transition ${previousState}→${nextState}`, lastTransition);
  dispatchConnectivityChanged(previousState);
  exposeConnectivityE2eHooks();
  scheduleHealthCheck();
}

export function getConnectivityState() {
  return state;
}

export function isGoodConnection() {
  return state === CONNECTIVITY_STATE.GOOD;
}

/**
 * True when the app should use offline/local-first behavior.
 * Poor connections are treated the same as offline.
 */
export function isEffectivelyOffline() {
  if (isBrowserOffline()) {
    return true;
  }
  return state === CONNECTIVITY_STATE.POOR;
}

export function canAttemptSync() {
  return !isBrowserOffline() && state === CONNECTIVITY_STATE.GOOD;
}

export function getConnectivitySnapshot() {
  return {
    state,
    consecutiveSuccesses,
    consecutiveFailures,
    browserOnline: !isBrowserOffline(),
    activeTransport: getActiveTransport(),
    wifiReachabilityFailed,
    lastTransition,
    lastFailure,
  };
}

export function subscribeConnectivity(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function resetCounters() {
  consecutiveSuccesses = 0;
  consecutiveFailures = 0;
}

function applyConnectivityFailure({
  reason = "network-error",
  latencyMs = 0,
  fromHealthCheck = false,
  httpStatus = null,
  source = null,
} = {}) {
  consecutiveSuccesses = 0;
  consecutiveFailures += 1;

  const resolvedSource =
    source || (fromHealthCheck ? "health-check" : "indirect");
  const failureFields = {
    reason,
    source: resolvedSource,
    httpStatus,
    latencyMs,
    fromHealthCheck,
  };

  recordLastFailure(failureFields);

  if (state === CONNECTIVITY_STATE.GOOD) {
    if (consecutiveFailures >= CONFIG.FAILURES_TO_POOR) {
      setState(CONNECTIVITY_STATE.POOR, failureFields);
      return;
    }

    logConnectivityEvent("request failure recorded (staying good)", {
      from: CONNECTIVITY_STATE.GOOD,
      to: CONNECTIVITY_STATE.GOOD,
      ...buildDiagnosticFields(failureFields),
    });
    exposeConnectivityE2eHooks();
    return;
  }

  if (state !== CONNECTIVITY_STATE.POOR) {
    setState(CONNECTIVITY_STATE.POOR, failureFields);
    return;
  }

  logConnectivityEvent("request failure recorded (already poor)", {
    from: CONNECTIVITY_STATE.POOR,
    to: CONNECTIVITY_STATE.POOR,
    ...buildDiagnosticFields(failureFields),
  });
  exposeConnectivityE2eHooks();
}

export function reportConnectivitySuccess(
  latencyMs = 0,
  { fromHealthCheck = false } = {},
) {
  if (isBrowserOffline()) {
    return;
  }

  if (latencyMs > CONFIG.SLOW_REQUEST_LATENCY_MS) {
    reportConnectivityFailure({
      reason: "slow",
      latencyMs,
      fromHealthCheck: true,
      source: fromHealthCheck ? "health-check" : "slow-path",
    });
    return;
  }

  consecutiveFailures = 0;
  consecutiveSuccesses += 1;

  const successesRequired = fromHealthCheck
    ? CONFIG.HEALTH_SUCCESSES_TO_GOOD
    : CONFIG.SUCCESSES_TO_GOOD;

  if (
    state === CONNECTIVITY_STATE.POOR &&
    consecutiveSuccesses >= successesRequired
  ) {
    wifiReachabilityFailed = false;
    setState(CONNECTIVITY_STATE.GOOD, {
      reason: fromHealthCheck ? "health-ok" : "recovered",
      source: fromHealthCheck ? "health-check" : "indirect",
      latencyMs,
      fromHealthCheck,
    });
  }
}

export function reportConnectivityFailure({
  reason = "network-error",
  latencyMs = 0,
  fromHealthCheck = false,
  httpStatus = null,
  source = null,
} = {}) {
  if (isBrowserOffline()) {
    return;
  }

  // Wi-Fi priority: indirect failures defer to the health probe while Wi-Fi
  // is the active transport so weak cellular cannot force offline-first mode.
  if (!fromHealthCheck && getActiveTransport() === TRANSPORT_TYPE.WIFI) {
    if (started) {
      scheduleHealthCheck(0);
    }
    return;
  }

  applyConnectivityFailure({
    reason,
    latencyMs,
    fromHealthCheck,
    httpStatus,
    source,
  });
}

async function pingSupabase() {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!url || !key) {
    return {
      ok: false,
      reason: "missing-env",
      latencyMs: 0,
      httpStatus: null,
    };
  }

  const startedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    CONFIG.HEALTH_CHECK_TIMEOUT_MS,
  );

  try {
    const response = await fetch(`${url}/auth/v1/health`, {
      method: "GET",
      headers: {
        apikey: key,
      },
      signal: controller.signal,
      cache: "no-store",
    });

    const latencyMs =
      (typeof performance !== "undefined" ? performance.now() : Date.now()) -
      startedAt;

    if (!response.ok) {
      return {
        ok: false,
        reason: `health-http-${response.status}`,
        latencyMs,
        httpStatus: response.status,
      };
    }

    return { ok: true, latencyMs, httpStatus: response.status };
  } catch (error) {
    const latencyMs =
      (typeof performance !== "undefined" ? performance.now() : Date.now()) -
      startedAt;
    if (error?.name === "AbortError") {
      return { ok: false, reason: "timeout", latencyMs, httpStatus: null };
    }
    return {
      ok: false,
      reason: "network-error",
      latencyMs,
      httpStatus: null,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

function clearHealthCheckTimer() {
  if (healthCheckTimer != null) {
    clearTimeout(healthCheckTimer);
    healthCheckTimer = null;
  }
}

function scheduleHealthCheck(delayMs = null) {
  if (!started) {
    return;
  }

  clearHealthCheckTimer();

  if (state === CONNECTIVITY_STATE.OFFLINE) {
    return;
  }

  const interval =
    delayMs ??
    (state === CONNECTIVITY_STATE.POOR
      ? CONFIG.HEALTH_CHECK_INTERVAL_POOR_MS
      : CONFIG.HEALTH_CHECK_INTERVAL_GOOD_MS);

  healthCheckTimer = setTimeout(() => {
    runHealthCheck();
  }, interval);
}

async function runHealthCheck() {
  if (!started || healthCheckInFlight) {
    return;
  }

  const transport = getActiveTransport();

  if (transport === TRANSPORT_TYPE.NONE || isBrowserOffline()) {
    handleBrowserOffline({
      reason: isBrowserOffline() ? "browser-offline" : "transport-none",
      source: "health-check",
    });
    return;
  }

  healthCheckInFlight = true;

  try {
    const result = await pingSupabase();

    if (result.ok) {
      wifiReachabilityFailed = false;
      reportConnectivitySuccess(result.latencyMs, { fromHealthCheck: true });
      return;
    }

    if (transport === TRANSPORT_TYPE.WIFI) {
      wifiReachabilityFailed = true;
    }

    reportConnectivityFailure({
      ...result,
      fromHealthCheck: true,
      source: "health-check",
    });
  } finally {
    healthCheckInFlight = false;
    scheduleHealthCheck();
  }
}

function handleBrowserOffline({
  reason = "browser-offline",
  source = "browser-event",
} = {}) {
  resetCounters();
  wifiReachabilityFailed = false;
  recordLastFailure({
    reason,
    source,
    latencyMs: 0,
    fromHealthCheck: source === "health-check",
  });
  setState(CONNECTIVITY_STATE.OFFLINE, {
    reason,
    source,
    latencyMs: 0,
    fromHealthCheck: source === "health-check",
  });
}

function onBrowserOfflineEvent() {
  handleBrowserOffline({
    reason: "browser-offline",
    source: "browser-event",
  });
}

function handleBrowserOnline() {
  resetCounters();
  wifiReachabilityFailed = false;

  if (state === CONNECTIVITY_STATE.OFFLINE) {
    setState(CONNECTIVITY_STATE.POOR, {
      reason: "browser-online",
      source: "browser-event",
      latencyMs: 0,
      fromHealthCheck: false,
    });
  }

  // Probe immediately; first successful health probe restores good.
  scheduleHealthCheck(0);
}

function handleConnectionChange() {
  if (isBrowserOffline()) {
    return;
  }

  const transport = getActiveTransport();

  if (transport === TRANSPORT_TYPE.WIFI) {
    wifiReachabilityFailed = false;
  }

  if (
    transport === TRANSPORT_TYPE.CELLULAR &&
    wifiReachabilityFailed
  ) {
    resetCounters();
  }

  scheduleHealthCheck(0);
}

function attachConnectionListener() {
  const connection = getNetworkConnection();
  if (!connection || connectionChangeHandler) {
    return;
  }

  connectionChangeHandler = handleConnectionChange;
  connection.addEventListener("change", connectionChangeHandler);
}

function detachConnectionListener() {
  const connection = getNetworkConnection();
  if (!connection || !connectionChangeHandler) {
    return;
  }

  connection.removeEventListener("change", connectionChangeHandler);
  connectionChangeHandler = null;
}

export function startConnectivityManager() {
  if (started || typeof window === "undefined") {
    return;
  }

  started = true;
  state = resolveInitialState();
  wifiReachabilityFailed = false;
  lastTransition = null;
  lastFailure = null;
  exposeConnectivityE2eHooks();

  window.addEventListener("online", handleBrowserOnline);
  window.addEventListener("offline", onBrowserOfflineEvent);
  attachConnectionListener();

  scheduleHealthCheck(0);
}

export function stopConnectivityManager() {
  if (!started || typeof window === "undefined") {
    return;
  }

  started = false;
  clearHealthCheckTimer();
  wifiReachabilityFailed = false;
  window.removeEventListener("online", handleBrowserOnline);
  window.removeEventListener("offline", onBrowserOfflineEvent);
  detachConnectionListener();
}

/** @internal Test helper */
export function __resetConnectivityManagerForTests(nextState = CONNECTIVITY_STATE.GOOD) {
  stopConnectivityManager();
  state = nextState;
  resetCounters();
  wifiReachabilityFailed = false;
  lastTransition = null;
  lastFailure = null;
  listeners.clear();
}

/** @internal Test helper */
export function __setWifiReachabilityFailedForTests(value) {
  wifiReachabilityFailed = value;
}

function exposeConnectivityE2eHooks() {
  if (
    typeof window === "undefined" ||
    process.env.REACT_APP_E2E_CONNECTIVITY_HOOKS !== "true"
  ) {
    return;
  }

  window.__CP_CONNECTIVITY__ = {
    getState: () => getConnectivityState(),
    getSnapshot: () => getConnectivitySnapshot(),
    getActiveTransport: () => getActiveTransport(),
    getLastTransition: () => lastTransition,
    getLastFailure: () => lastFailure,
    reportSuccess: (options = {}) => {
      const latencyMs =
        typeof options === "number" ? options : options.latencyMs ?? 100;
      const fromHealthCheck =
        typeof options === "object" && options
          ? Boolean(options.fromHealthCheck)
          : false;
      reportConnectivitySuccess(latencyMs, { fromHealthCheck });
    },
    reportFailure: (options = {}) => {
      reportConnectivityFailure({
        reason: options.reason || "network-error",
        latencyMs: options.latencyMs ?? 0,
        httpStatus: options.httpStatus ?? null,
        fromHealthCheck: options.fromHealthCheck !== false,
        source: options.source || "e2e-hook",
      });
    },
  };
}
